import * as React from 'react';
import { flatMap, fromPairs, groupBy, isEqual } from 'lodash';
import Form from 'antd/lib/form';
import Tabs from 'antd/lib/tabs';
import ConfigProvider from 'antd/lib/config-provider';
import { NamePath } from 'antd/lib/form/interface';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import InputNumber from 'antd/lib/input-number';
import Cascader from 'antd/lib/cascader';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Checkbox from 'antd/lib/checkbox/Checkbox';
import Popover from 'antd/lib/popover';
import Typography from 'antd/lib/typography';
import { DeleteOutlined, InfoCircleOutlined, PlusOutlined, WarningFilled } from '@ant-design/icons';
import Tooltip from 'antd/lib/tooltip';
import {
    AnalyticsProperty,
    DependentOptions,
    IntegrationType,
    MicrosoftTeamsChannel,
    PatternToken,
    SettingField,
    SettingOption,
    SettingFieldType,
    SettingHelpTextBehavior,
} from '../types';
import TooltipInfoIcon from '../components/helperComponents/TooltipInfoIcon';
import { PatternTokenHelperTextComponent } from '../components/appSettings/PatternTokenHelperTextComponent';
import SupportedTokensComponent from '../components/common/SupportedTokensComponent';
import { TeamsChannelOverrideSelect } from '../components/sharedComponents/TeamsChannelOverrideSelect';
import { antGrey, runwayColors } from '../components/styles/globalStyles';
import { EventsAndParamsInput } from '../components/integrations/EventsAndParamsInput';
import { FieldType, filterOptionLabel, FormFieldData, renderFormField } from './formHelpers';

const INFO_ICON_GAP = 8;
const SETTING_FIELD_MIN_ROW_WIDTH = 120;
const SETTING_FIELD_TAB_CONTENT_STYLE: React.CSSProperties = {
    background: runwayColors.white,
    borderRadius: '0 0 8px 8px',
    padding: 16,
    border: `1px solid ${antGrey.grey4}`,
    borderTop: 'none',
};

// Keys are dot-separated payload paths, which antd writes back as nested objects
const getSettingFieldNamePath = (key: string): NamePath => (key.includes('.') ? key.split('.') : key);

// The integration type only picks the events widget's wording
export const formFieldsFromSettingFields = (
    settingFields: SettingField[],
    integrationType: IntegrationType,
): FormFieldData[] => {
    const lines: SettingFieldLine[] = [];
    for (const variants of Object.values(groupBy(settingFields, 'key'))) {
        const formField = formFieldFromSettingField(variants[0], integrationType);
        const settingFormField =
            variants.some((field) => field.visibleWhenEquals || field.visibleWhenAnySet || field.disabledWhenEquals) ?
                conditionalSettingField(formField, variants, integrationType)
            :   formField;
        const previousLine = lines[lines.length - 1];
        if (variants[0].isInlineWithPrevious && previousLine) {
            previousLine.formFields.push(settingFormField);
        } else {
            lines.push({ tab: variants[0].tab, formFields: [settingFormField] });
        }
    }
    return groupSettingFieldTabs(lines);
};

interface SettingFieldLine {
    tab?: string;
    formFields: FormFieldData[];
}

interface SettingFieldTabPane {
    tab: string;
    formFields: FormFieldData[];
}

// Consecutive lines with tabs share one set of tabs, ordered by where each tab first appears
const groupSettingFieldTabs = (lines: SettingFieldLine[]): FormFieldData[] => {
    const formFields: FormFieldData[] = [];
    let panes: SettingFieldTabPane[] = [];
    for (const [index, line] of lines.entries()) {
        const lineFormField = buildSettingFieldLine(line.formFields);
        if (!line.tab) {
            formFields.push(lineFormField);
            continue;
        }

        const pane = panes.find((existingPane) => existingPane.tab === line.tab);
        if (pane) {
            pane.formFields.push(lineFormField);
        } else {
            panes.push({ tab: line.tab, formFields: [lineFormField] });
        }

        const isLastTabbedLine = !lines[index + 1]?.tab;
        if (isLastTabbedLine) {
            formFields.push(buildSettingFieldTabs(panes));
            panes = [];
        }
    }
    return formFields;
};

// Every tab stays mounted, so fields on other tabs still submit and validate
const buildSettingFieldTabs = (panes: SettingFieldTabPane[]): FormFieldData => ({
    fieldType: FieldType.WrappedGroup,
    // Listed flat so the form still seeds each tab's initial values
    groupedFields: flatMap(panes, (pane) => pane.formFields),
    groupChildrenWrapper: (_children, _fields, renderFields) => (
        <Tabs
            type="card"
            tabBarStyle={{ marginBottom: 0 }}
            items={panes.map((pane) => ({
                key: pane.tab,
                label: pane.tab,
                forceRender: true,
                children: <div style={SETTING_FIELD_TAB_CONTENT_STYLE}>{renderFields(pane.formFields)}</div>,
            }))}
        />
    ),
});

// Fields of a row share it by their widths and wrap when it's too narrow; a hidden field leaves its space to the rest
const buildSettingFieldLine = (formFields: FormFieldData[]): FormFieldData =>
    formFields.length === 1 ?
        formFields[0]
    :   {
            fieldType: FieldType.WrappedGroup,
            groupedFields: formFields,
            groupChildrenWrapper: (children) => (
                <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 8, width: '100%' }}>{children}</div>
            ),
        };

const formFieldFromSettingField = (settingField: SettingField, integrationType: IntegrationType): FormFieldData => {
    const help = getSettingFieldHelp(settingField);
    const isListEditor =
        settingField.type === SettingFieldType.KeyValue ||
        settingField.type === SettingFieldType.SecretKeyValue ||
        settingField.type === SettingFieldType.Rows;

    return {
        name: getSettingFieldNamePath(settingField.key),
        label: getSettingFieldLabel(settingField),
        tooltip: help.tooltip,
        initialValue: getSettingFieldInitialValue(settingField),
        fieldType: FieldType.Custom,
        rules: getSettingFieldRules(settingField),
        helpMessage: withSettingFieldResetButton(
            settingField,
            withSettingFieldWarning(settingField.warningText, help.message),
        ),
        extraFormItemProps: {
            ...(settingField.type === SettingFieldType.Switch && { valuePropName: 'checked' }),
            // List editors hold several inputs, which shouldn't each show the field's feedback icon
            ...(isListEditor && { hasFeedback: false }),
            ...(settingField.requiredWhenAnySet && {
                dependencies: settingField.requiredWhenAnySet.map(getSettingFieldNamePath),
            }),
        },
        style:
            settingField.width !== undefined ?
                { flex: `${settingField.width} 1 0`, minWidth: SETTING_FIELD_MIN_ROW_WIDTH }
            :   undefined,
        renderField: () => renderSettingFieldInput(settingField, integrationType),
    };
};

// Switches and key/value lists show their own label and help text
const hasInlineLabel = (settingField: SettingField): boolean =>
    settingField.type === SettingFieldType.Switch || settingField.type === SettingFieldType.KeyValue;

const getSettingFieldLabel = (settingField: SettingField): string => {
    if (hasInlineLabel(settingField)) {
        return '';
    }
    // A blank label keeps the input level with its neighbor's
    if (settingField.isLabelHidden) {
        return ' ';
    }
    return settingField.label;
};

const getSettingFieldHelp = (settingField: SettingField): { tooltip?: string; message?: React.ReactNode } => {
    const { helpText, patternTokens, helpTextBehavior } = settingField;
    if (hasInlineLabel(settingField)) {
        return helpTextBehavior === SettingHelpTextBehavior.Below ? { message: helpText } : {};
    }
    // Key/value lists list their tokens themselves; other fields list them in place of the help text
    if (patternTokens) {
        // A text area's help reads before its tokens, while a one-line input keeps it in a tooltip
        const isHelpBeforeTokens = settingField.type === SettingFieldType.TextArea;
        return {
            tooltip: isHelpBeforeTokens ? undefined : helpText,
            message: (
                <SupportedTokensComponent
                    availableTokens={patternTokens}
                    prefixText={isHelpBeforeTokens ? helpText : undefined}
                    secondary
                />
            ),
        };
    }
    if (helpTextBehavior) {
        return helpTextBehavior === SettingHelpTextBehavior.Tooltip ? { tooltip: helpText } : { message: helpText };
    }
    // Fields sharing a row keep their help in a tooltip, so their inputs line up
    if (settingField.width !== undefined) {
        return { tooltip: helpText };
    }
    return { message: helpText };
};

// Mirrors the backend's rows check, so a missing cell or a repeated row shows next to the field
const validateSettingRows = (settingField: SettingField, rows: Row[]): Promise<void> => {
    for (const [index, row] of rows.entries()) {
        const missingColumn = settingField.columns?.find(
            (column) => column.required && isEmptySettingValue(row[column.key]),
        );
        if (missingColumn) {
            return Promise.reject(new Error(`${missingColumn.label} of row ${index + 1} is required`));
        }
        if (rows.slice(0, index).some((previousRow) => isEqual(previousRow, row))) {
            return Promise.reject(new Error(`Row ${index + 1} is listed twice`));
        }
    }
    return Promise.resolve();
};

// antd only counts an empty list as missing for array rules
const getSettingFieldRules = (settingField: SettingField): FormFieldData['rules'] => {
    const requiredRule =
        isListSettingField(settingField) ? { required: true, type: 'array' as const } : { required: true };
    if (settingField.type === SettingFieldType.Rows) {
        return [{ validator: (_, rows: Row[] | undefined) => validateSettingRows(settingField, rows ?? []) }];
    }
    if (settingField.required) {
        return [requiredRule];
    }
    const requiredWhenAnySet = settingField.requiredWhenAnySet ?? [];
    if (requiredWhenAnySet.length === 0) {
        return [];
    }
    return [
        ({ getFieldValue }) => ({
            ...requiredRule,
            required: requiredWhenAnySet.some(
                (key) => !isEmptySettingValue(getFieldValue(getSettingFieldNamePath(key))),
            ),
        }),
    ];
};

const isListSettingField = (settingField: SettingField): boolean =>
    settingField.type === SettingFieldType.MultiSelect ||
    settingField.type === SettingFieldType.EventsAndParams ||
    settingField.type === SettingFieldType.Tags ||
    settingField.type === SettingFieldType.MultiCascader ||
    settingField.type === SettingFieldType.TeamsChannels;

// A cascader's value is [parent, child]; its form item holds the child
const getSettingFieldInitialValue = (settingField: SettingField): unknown => {
    if (settingField.type === SettingFieldType.Cascader) {
        return settingField.currentValue?.[1];
    }
    return settingField.currentValue ?? settingField.defaultValue;
};

const renderSettingFieldInput = (settingField: SettingField, integrationType: IntegrationType): React.ReactNode => {
    switch (settingField.type) {
        case SettingFieldType.Switch:
            return (
                <Checkbox>
                    {settingField.label}
                    {settingField.helpText && settingField.helpTextBehavior !== SettingHelpTextBehavior.Below && (
                        <span style={{ marginInlineStart: INFO_ICON_GAP }}>
                            <TooltipInfoIcon tooltipContent={settingField.helpText} />
                        </span>
                    )}
                </Checkbox>
            );
        case SettingFieldType.Cascader:
            return <SettingFieldCascader settingField={settingField} />;
        case SettingFieldType.Select:
        case SettingFieldType.MultiSelect: {
            const mode = settingField.type === SettingFieldType.MultiSelect ? 'multiple' : undefined;
            if (settingField.optionsBy) {
                return <DependentOptionsSelect optionsBy={settingField.optionsBy} mode={mode} />;
            }
            return (
                <Select
                    options={settingField.options ?? undefined}
                    mode={mode}
                    placeholder={settingField.placeholder ?? 'Select'}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    style={{ width: '100%' }}
                />
            );
        }
        case SettingFieldType.Tags:
            return (
                <Select
                    options={settingField.options ?? undefined}
                    mode="tags"
                    tokenSeparators={['\n']}
                    // Without suggestions there's nothing to list, so only typed values show
                    open={settingField.options ? undefined : false}
                    notFoundContent={null}
                    placeholder={settingField.placeholder ?? (settingField.options ? 'Select or enter' : 'Enter')}
                    allowClear
                    filterOption={filterOptionLabel}
                    style={{ width: '100%' }}
                />
            );
        case SettingFieldType.Input:
            return <Input autoComplete="off" placeholder={settingField.placeholder} />;
        case SettingFieldType.TextArea:
            return <Input.TextArea rows={4} placeholder={settingField.placeholder} />;
        case SettingFieldType.MultiCascader:
            return (
                <Cascader
                    multiple
                    showSearch
                    allowClear
                    expandTrigger="hover"
                    placeholder="Select"
                    options={getCascaderOptions(settingField)}
                    displayRender={(labels) => `${labels[1]} (${labels[0]})`}
                    style={{ width: '100%' }}
                />
            );
        case SettingFieldType.TeamsChannels:
            return (
                <TeamsChannelOverrideSelect
                    allChannels={getTeamsChannelsFromOptions(settingField)}
                    selectedChannels={[]}
                    teams={(settingField.options ?? []).map((team) => ({
                        id: team.value,
                        name: team.label,
                        description: '',
                    }))}
                    disabled={false}
                />
            );
        case SettingFieldType.Rows:
            return <SettingRowsInput settingField={settingField} />;
        case SettingFieldType.SecretKeyValue:
            return (
                <KeyValueInput
                    itemLabel={settingField.itemLabel ?? settingField.label}
                    redactedKeys={Object.keys(settingField.currentValue ?? {})}
                />
            );
        case SettingFieldType.Number:
            return <InputNumber precision={0} style={{ width: '100%' }} />;
        case SettingFieldType.EventsAndParams:
            return (
                <EventsAndParamsInput
                    integrationId={integrationType}
                    eventsAndParams={getEventsAndParamsSuggestions(settingField)}
                />
            );
        case SettingFieldType.KeyValue:
            return (
                <KeyValueInput
                    itemLabel={settingField.label}
                    helpText={settingField.helpText}
                    patternTokens={settingField.patternTokens}
                />
            );
    }
};

// Teams channels are offered per team, and the select lists them all
const getTeamsChannelsFromOptions = (settingField: SettingField): MicrosoftTeamsChannel[] =>
    flatMap(Object.values(settingField.optionsBy?.options ?? {}), (channels) =>
        channels.map((channel) => ({
            id: channel.value,
            name: channel.label,
            displayName: channel.label,
            isRedacted: false,
        })),
    );

// Every event suggests the same params; nil options mean no suggestions
const getEventsAndParamsSuggestions = (settingField: SettingField): Map<string, AnalyticsProperty[]> | undefined => {
    if (!settingField.options) {
        return undefined;
    }
    const params = (settingField.paramOptions ?? []).map((option) => ({
        id: String(option.value),
        name: option.label,
    }));
    return new Map(settingField.options.map((option) => [String(option.value), params]));
};

// Form.Item passes value and onChange through to the Select
const DependentOptionsSelect: React.FC<{
    optionsBy: DependentOptions;
    mode?: 'multiple';
    value?: unknown;
    onChange?: (value: unknown) => void;
}> = ({ optionsBy, mode, value, onChange }) => {
    const parentValue = Form.useWatch(getSettingFieldNamePath(optionsBy.field));
    const options = optionsBy.options?.[String(parentValue)] ?? [];

    // Drops values the new parent doesn't offer; skips the first parent value, which is the saved one loading in
    const previousParentValue = React.useRef(parentValue);
    React.useEffect(() => {
        if (previousParentValue.current === parentValue) {
            return;
        }
        const hadParentValue = previousParentValue.current !== undefined;
        previousParentValue.current = parentValue;
        if (!hadParentValue) {
            return;
        }
        const parentOptions = optionsBy.options?.[String(parentValue)] ?? [];
        const isOption = (item: unknown) => parentOptions.some((option) => isEqual(option.value, item));
        if (Array.isArray(value)) {
            onChange?.(value.filter(isOption));
        } else if (!isOption(value)) {
            onChange?.(undefined);
        }
    }, [parentValue, value, onChange, optionsBy]);

    return (
        <Select
            value={value}
            onChange={onChange}
            options={options}
            mode={mode}
            placeholder="Select"
            showSearch
            allowClear
            filterOption={filterOptionLabel}
            style={{ width: '100%' }}
        />
    );
};

// Form.Item passes the child value and onChange; the parent is written to its own key
const SettingFieldCascader: React.FC<{
    settingField: SettingField;
    value?: string;
    onChange?: (value: string | undefined) => void;
}> = ({ settingField, value, onChange }) => {
    const form = Form.useFormInstance();
    const parentPath = getSettingFieldNamePath(settingField.optionsBy?.field ?? '');
    const parentValue = Form.useWatch(parentPath);
    const options = getCascaderOptions(settingField);

    return (
        <>
            {/* Registers the parent, which not every definition has as its own field */}
            <Form.Item name={parentPath} initialValue={settingField.currentValue?.[0]} hidden noStyle>
                <Input />
            </Form.Item>
            <Cascader
                allowClear
                expandTrigger="hover"
                placeholder="Select"
                options={options}
                value={value && parentValue ? [parentValue, value] : undefined}
                onChange={(pair) => {
                    form.setFieldValue(parentPath, pair?.[0]);
                    onChange?.(pair?.[1] as string | undefined);
                }}
                style={{ width: '100%' }}
            />
        </>
    );
};

// Parents without children can't complete a pair, so they're disabled
const getCascaderOptions = (settingField: SettingField) => {
    const childOptionsByParent = settingField.optionsBy?.options ?? {};
    return (settingField.options ?? []).map((parentOption) => {
        const children = childOptionsByParent[String(parentOption.value)] ?? [];
        return { ...parentOption, children, disabled: children.length === 0 };
    });
};

// Build Distro bucket rules save the picked workflow as one plain value ([parent, workflow] for a cascader),
// so they get simple inputs built from the release candidate workflow and subworkflow fields
export const getBuildDistroWorkflowPickerFields = (settingFields: SettingField[]): FormFieldData[] => {
    const workflowVariants = settingFields.filter((field) => field.key === 'workflowId');
    const workflowField =
        workflowVariants.find((field) => field.type === SettingFieldType.Cascader) ??
        workflowVariants.find((field) => field.type === SettingFieldType.Select) ??
        workflowVariants[0];
    const subWorkflowField = settingFields.find((field) => field.key === 'subWorkflowId');
    return [workflowField, subWorkflowField]
        .filter((field): field is SettingField => !!field)
        .map((field) => ({
            label: field.label,
            name: field.key,
            fieldType: FieldType.Custom,
            renderField: (_: unknown, extraProperties?: object) =>
                renderBuildDistroWorkflowInput(field, extraProperties),
        }));
};

const renderBuildDistroWorkflowInput = (settingField: SettingField, extraProperties?: object): React.ReactNode => {
    switch (settingField.type) {
        case SettingFieldType.Cascader:
            return (
                <Cascader
                    allowClear
                    expandTrigger="hover"
                    placeholder="Select"
                    options={getCascaderOptions(settingField)}
                    {...extraProperties}
                />
            );
        case SettingFieldType.Select:
            return (
                <Select
                    options={settingField.options ?? undefined}
                    showSearch
                    allowClear
                    filterOption={filterOptionLabel}
                    {...extraProperties}
                />
            );
        default:
            return <Input autoComplete="off" {...extraProperties} />;
    }
};

type Row = Record<string, unknown>;

interface RowColumn {
    key: string;
    getPlaceholder: (row: Row) => string;
    // A saved secret shows its placeholder like a value, since the value itself never reaches the browser
    isRedacted?: (row: Row) => boolean;
    options?: SettingOption[];
}

// Rows of inputs with add and remove buttons; a column with options is a select
const RowsEditor: React.FC<{
    itemLabel: string;
    helpText?: string;
    columns: RowColumn[];
    rows: Row[];
    newRow: Row;
    footer?: React.ReactNode;
    onRowsChange: (rows: Row[]) => void;
}> = ({ itemLabel, helpText, columns, rows, newRow, footer, onRowsChange }) => {
    const hasRows = rows.length > 0;
    return (
        <Space direction="vertical" style={{ width: '100%', paddingBottom: 16 }} size={16}>
            {rows.map((row, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {columns.map((column) =>
                        column.options ?
                            <Select
                                key={column.key}
                                placeholder={column.getPlaceholder(row)}
                                options={column.options}
                                value={row[column.key]}
                                onChange={(cellValue) => onRowsChange(setRowCell(rows, index, column.key, cellValue))}
                                style={{ width: '100%' }}
                            />
                        :   <Input
                                key={column.key}
                                placeholder={column.getPlaceholder(row)}
                                classNames={{ input: column.isRedacted?.(row) ? 'redacted-input' : undefined }}
                                value={(row[column.key] as string) ?? ''}
                                onChange={(event) =>
                                    onRowsChange(setRowCell(rows, index, column.key, event.target.value))
                                }
                            />,
                    )}
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined style={{ color: runwayColors.statusRed }} />}
                        onClick={() => onRowsChange(rows.filter((_, i) => i !== index))}
                    />
                </div>
            ))}
            {hasRows && footer}
            <div style={{ display: 'flex', alignItems: 'center', gap: INFO_ICON_GAP, width: 'fit-content' }}>
                <Button
                    icon={<PlusOutlined style={{ color: runwayColors.primary }} />}
                    onClick={() => onRowsChange([...rows, newRow])}
                    size="small"
                    style={{ display: 'inline-flex', alignItems: 'center' }}
                >
                    {hasRows ? `Add another ${itemLabel.toLowerCase()}` : `Add ${itemLabel.toLowerCase()}`}
                </Button>
                {helpText && <TooltipInfoIcon tooltipContent={helpText} />}
            </div>
        </Space>
    );
};

const setRowCell = (rows: Row[], index: number, key: string, cellValue: unknown): Row[] =>
    rows.map((row, i) => (i === index ? { ...row, [key]: cellValue } : row));

// Edits a map as rows; rows without a key are kept while typing but left out of the value
const KeyValueInput: React.FC<{
    itemLabel: string;
    helpText?: string;
    patternTokens?: PatternToken[];
    // Saved secrets come back without values; leaving one empty keeps it
    redactedKeys?: string[];
    value?: Record<string, string>;
    onChange?: (value: Record<string, string>) => void;
}> = ({ itemLabel, helpText, patternTokens, redactedKeys, value, onChange }) => {
    const [rows, setRows] = React.useState<Row[]>(() => getKeyValueRows(value));
    // Resyncs when the form sets the value (reset, new data), but keeps rows the user is still typing a key for
    React.useEffect(() => {
        setRows((currentRows) =>
            isEqual(getKeyValueRowsValue(currentRows), value ?? {}) ? currentRows : getKeyValueRows(value),
        );
    }, [value]);

    const columns: RowColumn[] = [
        { key: 'key', getPlaceholder: () => 'Key' },
        {
            key: 'value',
            getPlaceholder: (row) => (redactedKeys?.includes(row.key as string) ? '••••••••••••' : 'Value'),
            isRedacted: (row) => !!redactedKeys?.includes(row.key as string),
        },
    ];
    const tokensFooter = patternTokens && (
        <Typography.Text type="secondary">
            Tokenized patterns are supported.
            <Popover content={<PatternTokenHelperTextComponent availableTokens={patternTokens} />}>
                <InfoCircleOutlined style={{ color: runwayColors.darkTextGrey, marginInlineStart: INFO_ICON_GAP }} />
            </Popover>
        </Typography.Text>
    );
    return (
        <RowsEditor
            itemLabel={itemLabel}
            helpText={helpText}
            columns={columns}
            rows={rows}
            newRow={{ key: '', value: '' }}
            footer={tokensFooter}
            onRowsChange={(updatedRows) => {
                setRows(updatedRows);
                onChange?.(getKeyValueRowsValue(updatedRows));
            }}
        />
    );
};

const getKeyValueRows = (value: Record<string, string> | undefined): Row[] =>
    Object.entries(value ?? {}).map(([key, rowValue]) => ({ key, value: rowValue }));

const getKeyValueRowsValue = (rows: Row[]): Record<string, string> =>
    fromPairs(rows.filter((row) => row.key !== '').map((row) => [row.key as string, (row.value as string) ?? '']));

// Edits a list of objects, one per row, with a column per field of the row
const SettingRowsInput: React.FC<{
    settingField: SettingField;
    value?: Row[];
    onChange?: (value: Row[]) => void;
}> = ({ settingField, value, onChange }) => {
    const columns = settingField.columns ?? [];
    return (
        <RowsEditor
            itemLabel={settingField.itemLabel ?? settingField.label}
            columns={columns.map((column) => ({
                key: column.key,
                getPlaceholder: () => column.label,
                options: column.options ?? undefined,
            }))}
            rows={value ?? []}
            newRow={fromPairs(columns.map((column) => [column.key, column.defaultValue]))}
            onRowsChange={(rows) => onChange?.(rows)}
        />
    );
};

// One Form.Item owns each payload key, even when its input changes with another setting.
const conditionalSettingField = (
    formField: FormFieldData,
    variants: SettingField[],
    integrationType: IntegrationType,
): FormFieldData => {
    const dependencies = [
        ...new Set(
            flatMap(variants, (field) => [
                ...Object.keys(field.visibleWhenEquals ?? {}),
                ...(field.visibleWhenAnySet ?? []),
                ...Object.keys(field.disabledWhenEquals ?? {}),
            ]),
        ),
    ].map(getSettingFieldNamePath);

    return {
        ...formField,
        fieldType: FieldType.Custom,
        skipFormItemWrapper: true,
        renderField: () => (
            <Form.Item noStyle dependencies={dependencies}>
                {({ getFieldValue }) => {
                    const activeField = variants.find((field) => isSettingFieldVisible(field, getFieldValue));
                    const field = formFieldFromSettingField(activeField ?? variants[0], integrationType);
                    // A disabled field keeps its value and, like the backend, skips its rules
                    const isDisabled =
                        !!activeField?.disabledWhenEquals &&
                        matchesSettingValues(activeField.disabledWhenEquals, getFieldValue);
                    return (
                        <ConfigProvider componentDisabled={isDisabled}>
                            {renderFormField({
                                ...field,
                                dependencies,
                                rules: activeField && !isDisabled ? field.rules : [],
                                // Keep hidden values registered for submission, without running an inactive input's effects.
                                extraFormItemProps: { ...field.extraFormItemProps, hidden: !activeField },
                                renderField: activeField ? field.renderField : () => <Input />,
                            })}
                        </ConfigProvider>
                    );
                }}
            </Form.Item>
        ),
    };
};

const isSettingFieldVisible = (settingField: SettingField, getFieldValue: (name: NamePath) => unknown): boolean => {
    const matchesValues = matchesSettingValues(settingField.visibleWhenEquals ?? {}, getFieldValue);
    const visibleWhenAnySet = settingField.visibleWhenAnySet ?? [];
    const hasSetValue =
        visibleWhenAnySet.length === 0 ||
        visibleWhenAnySet.some((key) => !isEmptySettingValue(getFieldValue(getSettingFieldNamePath(key))));
    return matchesValues && hasSetValue;
};

const isEmptySettingValue = (value: unknown): boolean => {
    if (value == null || value === '') return true;
    if (typeof value === 'object') return Object.keys(value).length === 0;
    return false;
};

const matchesSettingValues = (
    conditions: Record<string, unknown>,
    getFieldValue: (name: NamePath) => unknown,
): boolean =>
    Object.entries(conditions).every(([key, expected]) =>
        isEqual(getFieldValue(getSettingFieldNamePath(key)), expected),
    );

const withSettingFieldWarning = (warningText: string | undefined, helpMessage: React.ReactNode): React.ReactNode =>
    warningText ?
        <>
            {helpMessage}
            <Tooltip title={warningText}>
                <WarningFilled style={{ color: runwayColors.statusYellow, marginInlineStart: INFO_ICON_GAP }} />
            </Tooltip>
        </>
    :   helpMessage;

const withSettingFieldResetButton = (settingField: SettingField, helpMessage: React.ReactNode): React.ReactNode =>
    settingField.isResettable ?
        <>
            {helpMessage}
            <SettingFieldResetButton settingField={settingField} />
        </>
    :   helpMessage;

const SettingFieldResetButton: React.FC<{ settingField: SettingField }> = ({ settingField }) => {
    const form = Form.useFormInstance();
    return (
        <div style={{ marginTop: INFO_ICON_GAP }}>
            <Button
                onClick={() => form.setFieldValue(getSettingFieldNamePath(settingField.key), settingField.defaultValue)}
            >
                Reset {settingField.label.toLowerCase()} to default
            </Button>
        </div>
    );
};
